<template>
	<!-- Under the picture, like OverlayControl and WatermarkControl: you judge a
	-- caption by looking at the picture it is on, so the controls belong beside
	-- it rather than three groups down a settings panel.
	--
	-- The library and the blank cards are NOT here. They are an alternative to
	-- dropping a file, so they belong beside the drop zone -- LibraryStrip.vue,
	-- which says more about why.
	--
	-- ONE BLOCK PER AREA, each with its own words, colour and size. They shared
	-- a colour and a size before, and sharing meant every choice was a
	-- compromise between two captions: one on the dark half of a photograph and
	-- one on the light half cannot both be white, and a long line beside a short
	-- one cannot both be the right size. Grouped rather than interleaved so it
	-- is never a question which swatch belongs to which words. -->
	<div class="flex flex-col gap-3 w-full">
		<div v-for="area in liveAreas" :key="area.slot" role="group"
			:aria-label="t(area.label)" class="flex flex-col gap-2">
			<div class="flex items-center gap-2">
				<input type="text" :maxlength="MAX_MEME_CHARS"
					:data-area="area.slot"
					:value="uiStore[area.text]"
					@input="uiStore[area.text] = $event.target.value"
					@change="$emit('change')"
					@keyup.enter="$event.target.blur()"
					:placeholder="t('meme.placeholder')"
					:aria-label="t(area.label)"
					class="input input-sm w-full rounded-lg" />

				<!-- UNDOING THE BUTTON ABOVE, and only on the area it opened.
				-- Beside the words rather than under them, because what it removes
				-- is this field and the rectangle drawn from it -- a row of its own
				-- at the foot of the panel would not say which.
				--
				-- It does NOT clear what was typed. The words stay in the store,
				-- out of the picture and out of the bake, so pressing Add again
				-- brings them back: a control that can throw away a sentence is
				-- worth making undoable, and the case it was asked for -- an area
				-- opened by accident -- has nothing in it anyway. -->
				<button v-if="area.slot === removableSlot" type="button"
					:data-remove-zone="area.slot"
					@click="removeZone()"
					:title="t('meme.removeZone')"
					:aria-label="t(area.label) + ': ' + t('meme.removeZone')"
					class="btn btn-sm btn-ghost shrink-0 rounded-lg px-2">&times;</button>
			</div>

			<div class="flex flex-wrap items-center gap-2">
				<button v-for="c in MEME_COLOURS" :key="c.hex"
					:data-area-colour="area.slot + ':' + c.key"
					@click.prevent="pick(area.color, c.hex)"
					:title="t('watermark.colour', { name: t('colours.' + c.key) })"
					:aria-label="t(area.label) + ': ' + t('watermark.colour', { name: t('colours.' + c.key) })"
					:aria-pressed="uiStore[area.color] === c.hex ? 'true' : 'false'"
					class="h-5 w-5 rounded-full border-2 transition"
					:class="uiStore[area.color] === c.hex ? 'border-white scale-110' : 'border-white/40'"
					:style="{ backgroundColor: c.hex }"></button>

				<!-- On the same row as the swatches rather than a line of its
				-- own: two areas each needing a labelled row of its own made
				-- the panel taller than the picture it is about. -->
				<input type="range" :min="MIN_MEME_SIZE * 100" :max="MAX_MEME_SIZE * 100" step="1"
					:data-area-size="area.slot"
					:value="Math.round(uiStore[area.size] * 100)"
					@input="uiStore[area.size] = Number($event.target.value) / 100"
					@change="$emit('change')"
					class="range range-sm min-w-[64px] grow"
					:aria-label="t(area.label) + ': ' + t('watermark.size')" />
			</div>
		</div>

		<!-- Shared, and deliberately: two typefaces on one meme is a different
		-- sort of decision, and nobody has asked for it. Each option is shown
		-- in its own face, so the list is a sample rather than nine names to
		-- imagine. -->
		<!-- ADDING A ZONE IS A DECISION, SO IT IS A BUTTON.
		-- It sits directly under the words it would join, above the face and
		-- the capitals, which are settings for whatever zones exist. It
		-- disappears once both are open rather than greying out: a control
		-- that can never do anything again is not worth the row. -->
		<button v-if="canAddZone" type="button" data-add-zone
			@click="addZone()"
			class="btn btn-sm btn-outline rounded-lg self-start">
			+ {{ t('meme.addZone') }}
		</button>

		<select data-meme-font :value="uiStore.memeFont"
			@change="uiStore.memeFont = $event.target.value; $emit('change')"
			:aria-label="t('watermark.font')"
			class="select select-sm rounded-lg w-full"
			:style="{ fontFamily: memeFontStack(uiStore.memeFont) }">
			<option v-for="f in MEME_FONTS" :key="f.key" :value="f.key"
				:style="{ fontFamily: f.stack }">{{ f.name }}</option>
		</select>

		<label class="flex items-center gap-2 text-[12px] slider-label">
			<input type="checkbox" data-meme-upper :checked="uiStore.memeUpper"
				@change="uiStore.memeUpper = $event.target.checked; $emit('change')"
				class="checkbox checkbox-sm" />
			<span>{{ t('meme.upper') }}</span>
		</label>
	</div>
</template>

<script setup>
import { useI18n } from 'vue-i18n'
import { MEME_COLOURS, MEME_SLOTS, memeSlots, memeStoreKeys, MEME_FONTS, MAX_MEME_CHARS,
         MIN_MEME_SIZE, MAX_MEME_SIZE, memeFontStack } from "../lib/meme"
import { computed } from "vue"
import { useUiElements } from "../store/uiElements"

const emit = defineEmits(['change'])
const uiStore = useUiElements()
const { t } = useI18n()

/* The areas, as data rather than as four copies of the same markup.
 *
 * `slot` is the internal name -- top, bottom, left and right describe the
 * DEFAULT band each one opens in, not where it has to stay, which is exactly
 * why what a visitor reads is Area 1 to Area 4. The numbering comes from the
 * position in MEME_SLOTS, so the panel cannot label an area differently from
 * the order lib/meme.js opens them in.
 *
 * The store keys come from memeStoreKeys() rather than being typed out here.
 * They used to be listed, so that a rename could not produce a field writing
 * to nothing -- at two areas that was eight names, and at four it is sixteen,
 * which is past the point where listing them is the safer of the two. One
 * derivation, checked by tests/meme_layout_test.mjs against the real store.
 */
const AREAS = MEME_SLOTS.map((slot, i) => {
	const k = memeStoreKeys(slot)
	return {slot, label: `meme.area${i + 1}`, text: k.text, color: k.color, size: k.size}
})

/* Only the zones on offer, and the same helper the rectangles and the stamps
 * ask -- so the panel cannot offer a field for a zone that is not drawn, nor
 * withhold one for a zone that is. */
const liveAreas = computed(() =>
	memeSlots(uiStore.memeZones).map((slot) => AREAS.find((a) => a.slot === slot)))

const canAddZone = computed(() => uiStore.memeZones < MEME_SLOTS.length)

/* WHICH AREA MAY BE REMOVED: the last one, and only while more than one is
 * open. The count is ordered -- lib/meme.js hands back the first N slots -- so
 * closing anything but the last would mean renumbering the areas under the
 * reader, and the rectangle they were looking at would become the other one. */
const removableSlot = computed(() =>
	uiStore.memeZones > 1 ? memeSlots(uiStore.memeZones).slice(-1)[0] : null)

/* Adding a zone is not the same as typing in it.
 *
 * It only widens what is on offer -- the second slot's words, colour and size
 * are whatever they already were, which is empty on a fresh picture and the
 * reader's own if they added it, removed it and added it again. Nothing is
 * reset, so the button cannot lose anything. */
function addZone() {
	if (!canAddZone.value) return
	uiStore.memeZones = Math.min(MEME_SLOTS.length, (uiStore.memeZones || 1) + 1)
}

/* And back again. Nothing is cleared -- see the button's own note -- so this
 * cannot lose anything, which is why it needs no confirmation. `change` is
 * emitted because it DOES change the file: a caption in a closed area is held
 * out of the bake, so removing an area with words in it is a re-render. */
function removeZone() {
	if (uiStore.memeZones <= 1) return
	uiStore.memeZones = uiStore.memeZones - 1
	emit("change")
}

// A colour is one gesture, so it asks for the work straight away; the slider is
// a drag, which is why it waits for `change`.
function pick(key, hex) {
	uiStore[key] = hex
	emit('change')
}
</script>

<style scoped>
/* These sit BELOW the picture, on the page's own background, so the colour has
 * to come from the theme -- the same correction OverlayControl's borrowed
 * text-white needed. The lettering swatches above are the opposite case and
 * keep their literal colours, which is why they are bound from lib/meme.js:
 * tests/test_palette.py forbids a raw hex in a .vue for that reason. */
.slider-label { color: var(--ink-mute); }
</style>
